import { useEffect, useRef, useState } from 'react';
import tuVanMoHinhPhuHopImage from '../../../assets/images/tuvanmohinhphuhop.jpg';
import thietKeTrienKhaiPipelineImage from '../../../assets/images/thietkecicdpipeline.jpg';
import tichHopBaoMatXuyenSuotImage from '../../../assets/images/tichhopbaomatxuyensuot.jpg';
import chuanHoaQuyTrinhImage from '../../../assets/images/chuanhoaquytrinhvanhanh.jpg';

interface SolutionItem {
  id: string;
  step?: string;
  icon: string;
  title: string;
  description: string;
  image?: string;
}

interface SolutionSectionProps {
  items?: SolutionItem[];
  loading?: boolean;
}

export default function SolutionSection({ items = [], loading = false }: SolutionSectionProps) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Fallback hardcoded data với images
  const defaultSolutions = [
    {
      id: 'default-1',
      step: '1',
      number: 1,
      icon: 'ri-compass-3-line',
      title: 'Tư vấn mô hình DevSecOps phù hợp',
      description: 'Phân tích hiện trạng công nghệ, quy mô team và mục tiêu kinh doanh. Đề xuất lộ trình triển khai phù hợp với mức độ trưởng thành công nghệ của doanh nghiệp.',
      image: tuVanMoHinhPhuHopImage
    },
    {
      id: 'default-2',
      step: '2',
      number: 2,
      icon: 'ri-git-branch-line',
      title: 'Thiết kế & triển khai CI/CD Pipeline tự động',
      description: 'Xây dựng pipeline tự động từ build, test đến deploy. Giảm 80% thời gian phát hành, loại bỏ sai sót do thao tác thủ công.',
      image: thietKeTrienKhaiPipelineImage
    },
    {
      id: 'default-3',
      step: '3',
      number: 3,
      icon: 'ri-shield-keyhole-line',
      title: 'Tích hợp bảo mật xuyên suốt vòng đời phát triển',
      description: 'Security scanning tự động ở mọi giai đoạn: code analysis, dependency check, container scanning, runtime protection. Phát hiện lỗ hổng trước khi production.',
      image: tichHopBaoMatXuyenSuotImage
    },
    {
      id: 'default-4',
      step: '4',
      number: 4,
      icon: 'ri-file-list-3-line',
      title: 'Chuẩn hóa quy trình phát triển & vận hành',
      description: 'Xây dựng quy trình chuẩn, tài liệu hóa best practices, đào tạo team. Dễ kiểm soát, dễ mở rộng, dễ bàn giao khi team phát triển.',
      image: chuanHoaQuyTrinhImage
    }
  ];

  // Map items từ API, thêm number và fallback images
  const solutions = items.length > 0 
    ? items.map((item, index) => ({
        ...item,
        number: parseInt(item.step || String(index + 1)) || index + 1,
        image: item.image || [tuVanMoHinhPhuHopImage, thietKeTrienKhaiPipelineImage, tichHopBaoMatXuyenSuotImage, chuanHoaQuyTrinhImage][index]
      }))
    : defaultSolutions;

  return (
    <section ref={sectionRef} id="solution" className="w-full overflow-x-hidden pt-6 md:pt-12 lg:pt-16 pb-6 md:pb-12 lg:pb-16 bg-[#E0F7FA]/15">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 w-full">
        {/* Section Header */}
        <div className={`text-center mb-12 md:mb-16 lg:mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-block mb-3 md:mb-4">
            <span className="text-xs md:text-sm font-semibold text-[#06b6d4] uppercase tracking-wider">
              GIẢI PHÁP CỦA STACKWAY
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2a4b7c] leading-tight mb-4 md:mb-6 px-4">
            Giải pháp DevSecOps từ StackWay
          </h2>
          <p className="text-base md:text-lg text-[#64748B] max-w-[700px] mx-auto leading-relaxed px-4">
            Quy trình 4 bước giúp doanh nghiệp triển khai DevSecOps đúng cách, phù hợp với quy mô và mục tiêu kinh doanh
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line - hidden on mobile */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-[#06b6d4] via-[#00b587] to-[#06b6d4] -translate-x-1/2"></div>

          {/* Steps */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-16 h-16 border-4 border-[#06b6d4] border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-gray-600">Đang tải...</p>
            </div>
          ) : solutions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Chưa có nội dung</p>
            </div>
          ) : (
            <div className="space-y-12 md:space-y-16 lg:space-y-20">
              {solutions.map((solution, index) => (
              <div 
                key={index}
                className={`relative flex flex-col md:flex-row items-center gap-8 md:gap-12 lg:gap-16 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
              >
                {/* Content Box */}
                <div className={`w-full md:w-1/2 transition-all duration-1000 ${
                  isVisible 
                    ? 'opacity-100 translate-x-0' 
                    : index % 2 === 0 
                      ? 'opacity-0 -translate-x-10' 
                      : 'opacity-0 translate-x-10'
                } ${index % 2 === 0 ? 'md:text-right md:pr-8' : 'md:text-left md:pl-8'} text-center md:text-left`}
                style={{ transitionDelay: isVisible ? `${0.3 + index * 0.6 + (index % 2 === 1 ? 0.4 : 0)}s` : '0s' }}>
                  <div className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 lg:p-12 shadow-lg hover:shadow-xl transition-all duration-300">
                    {/* Icon */}
                    <div className={`inline-flex w-10 h-10 md:w-12 md:h-12 items-center justify-center mb-4 md:mb-5 ${index % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'}`}>
                      <i className={`${solution.icon} text-2xl md:text-3xl text-[#06b6d4]`}></i>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl md:text-2xl font-bold text-[#2a4b7c] mb-3 md:mb-4 leading-snug">
                      {solution.title}
                    </h3>

                    {/* Description */}
                    <p className="text-sm md:text-base text-[#475569] leading-relaxed">
                      {solution.description}
                    </p>
                  </div>
                </div>

                {/* Number Circle */}
                <div className={`absolute top-0 md:top-1/2 left-1/2 md:left-1/2 -translate-x-1/2 md:-translate-y-1/2 z-10 transition-all duration-1000 ${
                  isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
                }`}
                style={{ transitionDelay: isVisible ? `${0.7 + index * 0.6}s` : '0s' }}>
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-[#06b6d4] to-[#00b587] flex items-center justify-center border-4 border-white shadow-lg">
                    <span className="text-2xl md:text-3xl font-bold text-white">{solution.number}</span>
                  </div>
                </div>

                {/* Image */}
                <div className={`w-full md:w-1/2 transition-all duration-1000 ${
                  isVisible 
                    ? 'opacity-100 translate-x-0' 
                    : index % 2 === 0 
                      ? 'opacity-0 translate-x-10' 
                      : 'opacity-0 -translate-x-10'
                }`}
                style={{ transitionDelay: isVisible ? `${0.3 + index * 0.6 + (index % 2 === 0 ? 0.4 : 0)}s` : '0s' }}>
                  <div className={`${index % 2 === 0 ? 'md:pl-8' : 'md:pr-8'}`}>
                    <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-lg">
                      <div className="w-full h-48 md:h-64 lg:h-72 flex items-center justify-center">
                        <img 
                          src={solution.image || tuVanMoHinhPhuHopImage}
                          alt={solution.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
